import { useEffect, useRef, useState } from "react";
import { apiRequest } from "../../services/api";

export default function PcConnectorSetup({ device, accessToken }) {
  const [open, setOpen] = useState(false),
    [namespace, setNamespace] = useState(""),
    [source, setSource] = useState("14");
  const [reviewed, setReviewed] = useState(false),
    [connectors, setConnectors] = useState([]),
    [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [token, setToken] = useState(""),
    [copied, setCopied] = useState(false),
    [rotateId, setRotateId] = useState("");
  const generation = useRef(0),
    lock = useRef(false);
  const synthetic =
    device.code === "DEV-MDB-TD-PREVIEW" || device.manufacturer === "SYNTHETIC";
  useEffect(() => {
    let active = true;
    generation.current++;
    setToken("");
    setCopied(false);
    setReviewed(false);
    setNamespace("");
    setSource("14");
    setRotateId("");
    setError("");
    setReady(false);
    setConnectors([]);
    setBusy(false);
    lock.current = false;
    if (!synthetic && accessToken)
      apiRequest(`/biometric-devices/${device.id}/connectors`, { accessToken })
        .then((r) => {
          if (active) {
            setConnectors(Array.isArray(r) ? r : []);
            setReady(true);
          }
        })
        .catch(() => {
          if (active)
            setError(
              "Unable to check existing connectors. Refresh before creating a token.",
            );
        });
    return () => {
      active = false;
      generation.current++;
    };
  }, [device.id, accessToken, synthetic]);
  const matching = connectors.find(
    (c) =>
      c.namespace?.toLowerCase() === namespace.trim().toLowerCase() &&
      c.sourceDeviceId === source.trim(),
  );
  const valid =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      namespace.trim(),
    ) && /^\d{1,20}$/.test(source.trim());
  async function generate(event) {
    event.preventDefault();
    if (
      lock.current ||
      !ready ||
      !reviewed ||
      !valid ||
      synthetic ||
      device.isActive === false ||
      (matching && (!rotateId || matching.revokedAt))
    )
      return;
    const ticket = generation.current;
    lock.current = true;
    setBusy(true);
    setError("");
    setToken("");
    setCopied(false);
    try {
      const r = await apiRequest(
        `/biometric-devices/${device.id}/connectors${matching ? `/${matching.id}/rotate` : ""}`,
        {
          method: "POST",
          accessToken,
          ...(!matching
            ? {
                body: {
                  namespace: namespace.trim().toLowerCase(),
                  sourceDeviceId: source.trim(),
                },
              }
            : {}),
          retryOnUnauthorized: false,
        },
      );
      if (ticket !== generation.current) return;
      setToken(r.token);
      setReviewed(false);
      setRotateId("");
      setConnectors((current) =>
        matching ? current : [...current, { ...r, token: undefined }],
      );
    } catch (e) {
      if (ticket === generation.current) {
        setError(
          `${e.message || "Setup failed"}. If the request may have reached the server, refresh this page and review existing connectors before retrying. Do not generate a new namespace.`,
        );
        setReady(false);
      }
    } finally {
      if (ticket === generation.current) {
        lock.current = false;
        setBusy(false);
      }
    }
  }
  async function copy() {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
    } catch {
      setError("Clipboard unavailable. Select and copy the token manually.");
    }
  }
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-semibold text-slate-900">Windows PC connector</h2>
          <p className="text-sm text-slate-600">
            Your existing CHARMS admin creates a restricted upload token.
            Preview uploads only—not automatic attendance posting.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setOpen((v) => !v);
            setToken("");
            setCopied(false);
          }}
          className="rounded-lg border px-4 py-2 text-sm font-medium"
        >
          {open ? "Close setup" : "Set up PC connector"}
        </button>
      </div>
      {open &&
        (synthetic ? (
          <p className="text-sm text-amber-800">
            Select the real TD device, not this synthetic test device, to set up
            the Windows PC.
          </p>
        ) : (
          <>
            <p className="text-sm text-slate-700">
              Selected device: <strong>{device.name}</strong> · {device.code}.
              UUID: <code>{device.id}</code>
            </p>
            <ol className="list-decimal pl-5 space-y-2 text-sm text-slate-700">
              <li>
                On Windows, extract the package and run START-HERE.cmd once. If
                already configured, preserve its original config and state.
              </li>
              <li>
                Open connector.config.json. Copy its Namespace below exactly. Do
                not generate a new one. Confirm SourceDeviceId matches the MDB
                device (TD is 14).
              </li>
              <li>
                Confirm this is the correct real CHARMS device and that the API
                origin in the Windows config points to this deployed backend—not
                localhost on another PC.
              </li>
            </ol>
            <form onSubmit={generate} className="space-y-3">
              <label className="block text-sm">
                Namespace from Windows connector.config.json
                <input
                  aria-label="Windows connector namespace"
                  value={namespace}
                  disabled={busy || !!token}
                  onChange={(e) => {
                    setNamespace(e.target.value);
                    setReviewed(false);
                    setRotateId("");
                  }}
                  className="mt-1 block w-full rounded-lg border p-2 font-mono"
                  placeholder="Paste the existing Namespace"
                  autoComplete="off"
                />
              </label>
              <label className="block text-sm">
                Database SourceDeviceId
                <input
                  aria-label="Database source device ID"
                  value={source}
                  disabled={busy || !!token}
                  onChange={(e) => {
                    setSource(e.target.value);
                    setReviewed(false);
                    setRotateId("");
                  }}
                  className="mt-1 block w-32 rounded-lg border p-2"
                />
              </label>
              {matching && (
                <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
                  {matching.revokedAt ? (
                    "This connector is revoked. Do not recreate it or bypass its revocation; ask an administrator to review."
                  ) : (
                    <>
                      This connector already exists. Its old token cannot be
                      displayed again. If the PC already works, leave it alone.
                      <label className="mt-2 flex gap-2">
                        <input
                          type="checkbox"
                          checked={rotateId === matching.id}
                          disabled={busy || !!token}
                          onChange={(e) =>
                            setRotateId(e.target.checked ? matching.id : "")
                          }
                        />
                        Replace its token: I understand the old token stops
                        working immediately and the PC credential must be
                        updated under the same Windows account. Preserve all
                        checkpoints.
                      </label>
                    </>
                  )}
                </div>
              )}
              <label className="flex gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={reviewed}
                  disabled={busy || !!token}
                  onChange={(e) => setReviewed(e.target.checked)}
                />
                I verified the real device, the PC's original Namespace, source
                ID and server address. I will keep the token private.
              </label>
              <button
                disabled={
                  !ready ||
                  !valid ||
                  !reviewed ||
                  busy ||
                  !!token ||
                  device.isActive === false ||
                  !!matching?.revokedAt ||
                  (!!matching && rotateId !== matching.id)
                }
                className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
              >
                {busy
                  ? "Generating…"
                  : matching
                    ? "Replace connector token"
                    : "Generate PC connector token"}
              </button>
            </form>
            {token && (
              <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-3">
                <h3 className="font-semibold">
                  Copy this token now—it is shown only in this session
                </h3>
                <p className="text-sm">
                  Do not send it in chat, screenshots or email. It is not your
                  admin password. Nothing is stored in browser storage by this
                  setup panel.
                </p>
                <textarea
                  aria-label="One-time PC connector token"
                  readOnly
                  value={token}
                  rows={3}
                  spellCheck={false}
                  className="block w-full rounded-lg border p-2 font-mono text-sm"
                />
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={copy}
                    className="rounded-lg border bg-white px-3 py-2 text-sm"
                  >
                    {copied ? "Copied" : "Copy token"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setToken("");
                      setCopied(false);
                    }}
                    className="rounded-lg border px-3 py-2 text-sm"
                  >
                    Hide token
                  </button>
                </div>
                <p className="text-sm">
                  On Windows, open PowerShell in the extracted folder and run:
                </p>
                <pre className="overflow-auto rounded-lg bg-slate-900 p-3 text-xs text-white">
                  {
                    '& "$env:WINDIR\\SysWOW64\\WindowsPowerShell\\v1.0\\powershell.exe" -NoProfile -ExecutionPolicy Bypass -File .\\Initialize-CharmsConnector.ps1'
                  }
                </pre>
                <p className="text-sm">
                  Paste the token at the hidden prompt. Use the same Windows
                  user that will run sync. Supervise one preview upload before
                  installing the automatic task. Close this panel afterward;
                  clear your clipboard if needed.
                </p>
              </div>
            )}
            {connectors.length > 0 && (
              <details className="text-sm">
                <summary>
                  Existing connector identities ({connectors.length})
                </summary>
                <ul className="mt-2 space-y-1">
                  {connectors.map((c) => (
                    <li key={c.id}>
                      <code>{c.namespace}</code> · source {c.sourceDeviceId} ·{" "}
                      {c.revokedAt ? "Revoked" : "Active"}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </>
        ))}
      {open && error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
    </section>
  );
}
